import { useEffect, useState } from 'react';
import useAuth from 'Modules/firebase/use-auth';
import { captureException } from '@sentry/browser';
import { useAppDispatch, useAppSelector } from './index';
import { userActions } from 'store/user/userSlice';
import {
    useFirestoreUserQuery,
    useGetOrganizationQuery,
} from 'generated/graphql';

import { partnerActions } from 'store/partner/partnerSlice';
import { createPartnerState } from 'Utils/Partner/create-partner-state';
import { useOrgNavigate } from "app/routes/useOrgNavigate";
import useNotification from 'app/contexts/NotificationContext/hooks/useNotification';
import { selectPartnerData } from 'store/partner/partnerSelector';
import { selectUserData } from 'store/user/userSelector';
import { firestoreToUserData } from 'Utils/User/convertToUserData';
import { useOperator } from 'app/contexts/OperatorContext';

function useQualityInitializer(): { loading: boolean } {
    const { organizationId } = useOperator();
    const dispatch = useAppDispatch();
    const userData = useAppSelector(selectUserData);
    const organizationData = useAppSelector(selectPartnerData);

    const navigate = useOrgNavigate();
    const { showNotification } = useNotification();
    const auth = useAuth();

    const [loading, setLoading] = useState(true);

    const isLoadingAuth = !auth.isAuthenticated || (auth.loading && !auth.user);

    const userQuery = useFirestoreUserQuery({
        skip: isLoadingAuth,
        variables: {
            where: {
                email: auth.user?.email,
                organizationId: auth.user?.org_id || organizationId,
            },
        },
        onError: () => {
            showNotification({
                message: 'Could not initialize Quality',
                severity: 'error',
            });
        },
    });

    const organizationQuery = useGetOrganizationQuery({
        skip: isLoadingAuth || !userQuery.data?.firestoreUser?.instanceId,
        variables: {
            where: { id: userQuery.data?.firestoreUser?.instanceId },
        },
    });

    useEffect(() => {
        const isLoading = userQuery.loading || organizationQuery.loading;
        const qualityOrg = organizationQuery.data?.organization;
        const qualityUser = userQuery.data?.firestoreUser;

        if (!isLoading && qualityOrg && qualityUser) {
            const userData = firestoreToUserData(qualityUser);
            dispatch(userActions.SAVE({ userData, isAuth: true }));

            dispatch(
                partnerActions.SAVE(
                    createPartnerState(qualityOrg, qualityUser),
                ),
            );
        }
    }, [organizationQuery.loading, userQuery.loading, userQuery.data]);

    useEffect(() => {
        const authLoading = !auth.isAuthenticated || auth.loading;
        const loadingQueries =
            !authLoading || userQuery.loading || organizationQuery.loading;

        if (!loadingQueries) {
            const showAuthError = (msg: string) => {
                return navigate(`/auth/error?error=${encodeURI(msg)}`);
            };

            if (userQuery.error) {
                captureException(userQuery.error);
                showAuthError(userQuery.error.message);
            }

            if (organizationQuery.error) {
                captureException(organizationQuery.error);
                showAuthError(organizationQuery.error.message);
            }
        }
    }, [userQuery.error, organizationQuery.error]);

    useEffect(() => {
        if (userData && organizationData) {
            setLoading(false);
        }
    }, [userData, organizationData]);

    return { loading };
}

export default useQualityInitializer;
